import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import Home from '../pages/Home';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MenuSidebar from '../components/MenuSidebar';
import AboutPage from '../pages/AboutPage';
import ProjectDetailsPage from '../pages/ProjectDetailsPage';
import ProjectsPage from '../pages/ProjectsPage';
import ContactPage from '../pages/ContactPage';
import GalleryPage from '../pages/GalleryPage';
import LocationPage from '../pages/LocationPage';
import ConstructionPage from '../pages/ConstructionPage';
import CareersPage from '../pages/CareersPage';
import SupportPage from '../pages/SupportPage';
import PageTransition from '../components/common/PageTransition';
import Loader from '../components/common/Loader';
import { useDispatch } from 'react-redux';
import { getAllProjects } from '../redux/slice/project.slice';
import { getAllCategories } from '../redux/slice/category.slice';

const UserRoutes = () => {
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const scrollContainerRef = useRef(null);
    const lenisRef = useRef(null);
    const dispatch = useDispatch();

    // Handle initial loading with API calls
    useEffect(() => {
        const loadData = async () => {
            try {
                await Promise.all([
                    dispatch(getAllProjects()),
                    dispatch(getAllCategories())
                ]);
            } catch (error) {
                console.error("Failed to load initial data", error);
            } finally {
                setTimeout(() => {
                    setIsLoading(false);
                }, 1000);
            }
        };

        loadData();
    }, [dispatch]);

    // Hide header and footer on login page
    const hideLayout = location.pathname === '/login';

    // Close menu on route change and scroll to top
    useEffect(() => {
        setIsMenuOpen(false);

        // Handle scroll to top
        if (lenisRef.current) {
            lenisRef.current.scrollTo(0, { immediate: true });
        } else if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        window.scrollTo(0, 0);
    }, [location]);

    // Keep legacy handleScroll for fallback and scroll detection
    const handleScroll = (e) => {
        const scrollTop = e.target.scrollTop;
        if (scrollTop > 50) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    return (
        <div
            className="relative bg-black h-screen w-screen overflow-hidden"
            style={{ perspective: '2000px' }}
        >
            {!hideLayout && <MenuSidebar isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />}

            <div
                ref={scrollContainerRef}
                className={`h-full bg-white transition-all duration-700 ease-[cubic-bezier(0.76,0,0.24,1)] origin-[0%_50%] z-20 relative 
                    ${isMenuOpen
                        ? 'w-full translate-x-0 scale-100 shadow-none cursor-default rounded-none [transform:none] lg:w-[75%] lg:translate-x-[55%] lg:scale-[0.55] lg:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] lg:overflow-hidden lg:cursor-pointer lg:rounded-[40px] lg:[transform:rotateY(-15deg)]'
                        : 'w-full translate-x-0 scale-100 rounded-none overflow-y-scroll [transform:rotateY(0deg)]'
                    }`}
                style={{
                    scrollBehavior: 'smooth',
                    overscrollBehavior: 'contain'
                }}
                onScroll={handleScroll}
                onWheel={(e) => {
                    // Debug: Log wheel events
                    console.log('Wheel event detected:', e.deltaY);
                    // Don't prevent default - let native scroll handle it
                }}
                onClick={() => isMenuOpen && setIsMenuOpen(false)}
            >
                {!hideLayout && <Header onMenuClick={() => setIsMenuOpen(true)} isScrolledProp={isScrolled} />}

                <div className="min-h-full flex flex-col">
                    <div className="flex-1">
                        <PageTransition>
                            <Routes location={location} key={location.pathname}>
                                <Route path="/" element={<Home />} />
                                <Route path="/about" element={<AboutPage />} />
                                <Route path="/projects" element={<ProjectsPage />} />
                                <Route path="/project/:sku" element={<ProjectDetailsPage />} />
                                <Route path="/contact" element={<ContactPage />} />
                                <Route path="/gallery" element={<GalleryPage />} />
                                <Route path="/location" element={<LocationPage />} />
                                <Route path="/careers" element={<CareersPage />} />
                                <Route path="/support" element={<SupportPage />} />
                                {/* <Route path="/construction" element={<ConstructionPage />} /> */}
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </PageTransition>
                    </div>

                    {!hideLayout && <Footer />}
                </div>

                {/* Interaction blocker when open - pure visual overlay for pointer events */}
                {isMenuOpen && (
                    <div className="absolute inset-0 z-50 bg-transparent pointer-events-none" />
                )}</div>

            <AnimatePresence>
                {isLoading && (
                    <motion.div
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        className="fixed inset-0 z-[10000]"
                    >
                        <Loader />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default UserRoutes;
